"use client";

import { useState, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { Settings2, Volume2, Upload, Check, Play, Phone, Bell, BellOff, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/auth-context';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import {
  RINGTONE_PRESETS,
  DEFAULT_RINGTONE_PREFS,
  previewRingtone,
  getRingtonePrefs,
  saveRingtonePrefs,
  uploadCustomRingtone,
  type RingtonePrefs,
} from '@/services/ringtone';
import {
  SOUND_PRESETS,
  DEFAULT_PREFS as DEFAULT_NOTIFICATION_PREFS,
  previewSound,
  getNotificationSoundPrefs,
  announceWays,
  saveNotificationSoundPrefs,
  uploadCustomSound,
  type NotificationSoundPrefs,
} from '@/services/notification-sound';

type CallMode = 'ring' | 'silent' | 'flash' | 'ring+flash';

interface ChatSettingsPanelProps {
  onRingtonePrefsChange?: (prefs: RingtonePrefs) => void;
}

export function ChatSettingsPanel({ onRingtonePrefsChange }: ChatSettingsPanelProps) {
  const { user } = useAuth();

  // Call mode state
  const [callMode, setCallMode] = useState<CallMode>('ring');
  const [callModeLoading, setCallModeLoading] = useState(true);

  // Ringtone state
  const [ringtonePrefs, setRingtonePrefs] = useState<RingtonePrefs>(DEFAULT_RINGTONE_PREFS);
  const [ringtoneLoading, setRingtoneLoading] = useState(true);
  const ringtoneFileRef = useRef<HTMLInputElement>(null);
  const [uploadingRingtone, setUploadingRingtone] = useState(false);

  // Notification sound state
  const [notifPrefs, setNotifPrefs] = useState<NotificationSoundPrefs>(DEFAULT_NOTIFICATION_PREFS);
  const tCopi = useTranslations('copi');
  const [notifLoading, setNotifLoading] = useState(true);
  const notifFileRef = useRef<HTMLInputElement>(null);
  const [uploadingNotif, setUploadingNotif] = useState(false);

  // Load prefs on mount
  useEffect(() => {
    if (!user?.uid) return;

    // Load call mode
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'userProfiles', user.uid));
        const mode = snap.data()?.callMode as CallMode;
        setCallMode(mode || 'ring');
      } catch {
        setCallMode('ring');
      }
      setCallModeLoading(false);
    })();

    // Load ringtone prefs
    getRingtonePrefs(user.uid).then(p => {
      setRingtonePrefs(p);
      setRingtoneLoading(false);
      onRingtonePrefsChange?.(p);
    });

    // Load notification sound prefs
    getNotificationSoundPrefs(user.uid).then(p => {
      setNotifPrefs(p);
      setNotifLoading(false);
    });
  }, [user?.uid, onRingtonePrefsChange]);

  // ── Call mode handler ──────────────────────────────────────────────────────

  const updateCallMode = async (newMode: CallMode) => {
    setCallMode(newMode);
    if (user?.uid) {
      try {
        await setDoc(
          doc(db, 'userProfiles', user.uid),
          { callMode: newMode },
          { merge: true }
        );
      } catch (err) {
        console.error('Error saving call mode:', err);
      }
    }
  };

  // ── Ringtone handlers ──────────────────────────────────────────────────────

  const updateRingtone = (patch: Partial<RingtonePrefs>) => {
    const next = { ...ringtonePrefs, ...patch };
    setRingtonePrefs(next);
    onRingtonePrefsChange?.(next);
    if (user?.uid) saveRingtonePrefs(user.uid, next);
  };

  const handleRingtoneFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.uid) return;
    setUploadingRingtone(true);
    try {
      const url = await uploadCustomRingtone(user.uid, file);
      updateRingtone({ ringtoneId: 'custom', customRingtoneUrl: url });
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploadingRingtone(false);
      if (ringtoneFileRef.current) ringtoneFileRef.current.value = '';
    }
  };

  // ── Notification sound handlers ─────────────────────────────────────────────

  const updateNotif = (patch: Partial<NotificationSoundPrefs>) => {
    const next = { ...notifPrefs, ...patch };
    setNotifPrefs(next);
    if (user?.uid) saveNotificationSoundPrefs(user.uid, next);
  };

  const handleNotifFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.uid) return;
    setUploadingNotif(true);
    try {
      const url = await uploadCustomSound(user.uid, file);
      updateNotif({ soundId: 'custom', customSoundUrl: url });
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploadingNotif(false);
      if (notifFileRef.current) notifFileRef.current.value = '';
    }
  };

  if (ringtoneLoading || notifLoading || callModeLoading) {
    return (
      <div className="p-4 text-center text-xs text-muted-foreground">Loading settings...</div>
    );
  }

  return (
    <div className="p-3 space-y-5 max-h-[60vh] overflow-y-auto">
      {/* ── Call Mode Section ──────────────────────────────────────────────── */}
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <Phone className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Call Mode</h3>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => updateCallMode('ring')}
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-colors",
              callMode === 'ring'
                ? "bg-primary text-white"
                : "bg-secondary hover:bg-secondary/80 text-foreground"
            )}
          >
            <Bell className="h-4 w-4" />
            Ring
          </button>
          <button
            onClick={() => updateCallMode('silent')}
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-colors",
              callMode === 'silent'
                ? "bg-primary text-white"
                : "bg-secondary hover:bg-secondary/80 text-foreground"
            )}
          >
            <BellOff className="h-4 w-4" />
            Silent
          </button>
          <button
            onClick={() => updateCallMode('flash')}
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-colors",
              callMode === 'flash'
                ? "bg-primary text-white"
                : "bg-secondary hover:bg-secondary/80 text-foreground"
            )}
          >
            <Zap className="h-4 w-4" />
            Flash Only
          </button>
          <button
            onClick={() => updateCallMode('ring+flash')}
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-colors",
              callMode === 'ring+flash'
                ? "bg-primary text-white"
                : "bg-secondary hover:bg-secondary/80 text-foreground"
            )}
          >
            <Bell className="h-3.5 w-3.5" /><Zap className="h-3.5 w-3.5 -ml-1" />
            Ring + Flash
          </button>
        </div>
        <p className="text-[10px] text-muted-foreground">
          {callMode === 'ring' && 'Sound plays when a call arrives.'}
          {callMode === 'silent' && 'No sound or flash — screen notification only.'}
          {callMode === 'flash' && 'Screen flashes, no ringtone.'}
          {callMode === 'ring+flash' && 'Both ringtone and screen flash on incoming calls.'}
        </p>
      </section>

      <hr className="border-border" />

      {/* ── Ring Tone Section ──────────────────────────────────────────────── */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Ring Tone</h3>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="ring-enabled" className="text-xs text-muted-foreground">Enabled</Label>
            <Switch
              id="ring-enabled"
              checked={ringtonePrefs.enabled}
              onCheckedChange={(v) => updateRingtone({ enabled: v })}
            />
          </div>
        </div>

        {/* Preset buttons */}
        <div className="grid grid-cols-2 gap-1.5">
          {RINGTONE_PRESETS.map(p => (
            <button
              key={p.id}
              onClick={() => {
                updateRingtone({ ringtoneId: p.id });
                previewRingtone(p.id, undefined, ringtonePrefs.volume);
              }}
              className={cn(
                "flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs transition-colors text-left",
                ringtonePrefs.ringtoneId === p.id
                  ? "bg-primary text-white"
                  : "bg-secondary hover:bg-secondary/80"
              )}
            >
              {ringtonePrefs.ringtoneId === p.id ? (
                <Check className="h-3 w-3 flex-shrink-0" />
              ) : (
                <Play className="h-3 w-3 flex-shrink-0" />
              )}
              <span className="truncate">{p.label}</span>
            </button>
          ))}

          {/* Custom button */}
          <button
            onClick={() => ringtoneFileRef.current?.click()}
            disabled={uploadingRingtone}
            className={cn(
              "flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs transition-colors text-left col-span-2",
              ringtonePrefs.ringtoneId === 'custom'
                ? "bg-primary text-white"
                : "bg-secondary hover:bg-secondary/80"
            )}
          >
            <Upload className="h-3 w-3 flex-shrink-0" />
            <span>{uploadingRingtone ? 'Uploading...' : ringtonePrefs.ringtoneId === 'custom' ? 'Custom (uploaded)' : 'Upload Custom'}</span>
          </button>
          <input
            ref={ringtoneFileRef}
            type="file"
            accept="audio/*"
            className="hidden"
            onChange={handleRingtoneFileUpload}
          />
        </div>

        {/* Volume */}
        <div className="flex items-center gap-3">
          <Volume2 className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          <Slider
            min={0}
            max={100}
            step={5}
            value={[ringtonePrefs.volume]}
            onValueChange={([v]) => updateRingtone({ volume: v })}
            className="flex-1"
          />
          <span className="text-xs text-muted-foreground w-8 text-right">{ringtonePrefs.volume}%</span>
        </div>
      </section>

      <hr className="border-border" />

      {/* ── Notification Sound Section ─────────────────────────────────────── */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Message Sound</h3>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="notif-enabled" className="text-xs text-muted-foreground">Enabled</Label>
            <Switch
              id="notif-enabled"
              checked={notifPrefs.enabled}
              onCheckedChange={(v) => updateNotif({ enabled: v })}
            />
          </div>
        </div>

        {/* How a new message is announced while BB is open: the sound, coπ's voice, or both. */}
        <div className="space-y-1.5">
          <p className="text-xs font-semibold">{tCopi('announceTitle')}</p>
          {/* Any mix of the three (Gordon 2026-11-01). */}
          <div className="grid grid-cols-3 gap-1.5" role="group" aria-label={tCopi('announceTitle')}>
            {(() => {
              const ways = announceWays(notifPrefs);
              return ([
                ['sound', 'announceSound', 'announceSound'],
                ['voice', 'announceVoice', 'announceVoice'],
                ['vibrate', 'announceVibrate', 'announceVibrate'],
              ] as const).map(([key, field, label]) => {
                const on = ways[key];
                return (
                  <button key={key} type="button" aria-pressed={on}
                    onClick={() => updateNotif({ [field]: !on } as Partial<NotificationSoundPrefs>)}
                    className={cn('rounded-md border px-2 py-1.5 text-xs font-semibold',
                      on ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-foreground hover:bg-secondary')}>
                    {tCopi(label)}
                  </button>
                );
              });
            })()}
          </div>
          <p className="text-2xs text-muted-foreground">{tCopi('announceHint')}</p>
        </div>

        {/* Preset buttons */}
        <div className="grid grid-cols-2 gap-1.5">
          {SOUND_PRESETS.map(p => (
            <button
              key={p.id}
              onClick={() => {
                updateNotif({ soundId: p.id });
                previewSound(p.id, undefined, notifPrefs.volume);
              }}
              className={cn(
                "flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs transition-colors text-left",
                notifPrefs.soundId === p.id
                  ? "bg-primary text-white"
                  : "bg-secondary hover:bg-secondary/80"
              )}
            >
              {notifPrefs.soundId === p.id ? (
                <Check className="h-3 w-3 flex-shrink-0" />
              ) : (
                <Play className="h-3 w-3 flex-shrink-0" />
              )}
              <span className="truncate">{p.label}</span>
            </button>
          ))}

          {/* Custom button */}
          <button
            onClick={() => notifFileRef.current?.click()}
            disabled={uploadingNotif}
            className={cn(
              "flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs transition-colors text-left col-span-2",
              notifPrefs.soundId === 'custom'
                ? "bg-primary text-white"
                : "bg-secondary hover:bg-secondary/80"
            )}
          >
            <Upload className="h-3 w-3 flex-shrink-0" />
            <span>{uploadingNotif ? 'Uploading...' : notifPrefs.soundId === 'custom' ? 'Custom (uploaded)' : 'Upload Custom'}</span>
          </button>
          <input
            ref={notifFileRef}
            type="file"
            accept="audio/*"
            className="hidden"
            onChange={handleNotifFileUpload}
          />
        </div>

        {/* Volume */}
        <div className="flex items-center gap-3">
          <Volume2 className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          <Slider
            min={0}
            max={100}
            step={5}
            value={[notifPrefs.volume]}
            onValueChange={([v]) => updateNotif({ volume: v })}
            className="flex-1"
          />
          <span className="text-xs text-muted-foreground w-8 text-right">{notifPrefs.volume}%</span>
        </div>
      </section>
    </div>
  );
}
